import { QUERY_NAME, SERVICES } from "@lhd/constants";
import axios from "@lhd/libs/axios";
import { getCompanyId } from "@lhd/utility";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notification } from "antd";
import { Lesson } from "types/lesson.type";

export const useGetLessonList = (
  id: string,
  page?: number,
  pageSize?: number,
) => {
  const companyId = getCompanyId();
  return useQuery([QUERY_NAME.LESSON_LIST, companyId, id, page, pageSize], () =>
    axios({
      method: "get",
      url: `${SERVICES.E_LEARNING_COURSE}/${id}/lessons`,
      params: {
        ...(pageSize !== 0 && {
          page,
          per_page: pageSize,
        }),
        company_id: companyId,
      },
    }).then((res) => ({
      data: res.data as Lesson[],
      meta: {
        total: res.headers["x-total"] || 0,
        total_page: res.headers["x-total-pages"] || 0,
      },
    })),
  );
};

export const useGetLessonDetail = (id?: string) => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.LESSON_DETAIL, companyId, id],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.E_LEARNING_LESSON}/${id}`,
        params: {
          company_id: companyId,
        },
      }).then((res) => ({
        data: res.data as Lesson,
      })),
    {
      cacheTime: 100,
      staleTime: 100,
      enabled: !!id,
    },
  );
};

export const useUpdateLessonDetail = (
  id?: string | number,
  onSubmitSuccess?: () => void,
) => {
  const isUpdate = !!id;
  const companyId = getCompanyId();
  const queryClient = useQueryClient();
  const url = isUpdate
    ? SERVICES.E_LEARNING_LESSON + "/" + id
    : SERVICES.E_LEARNING_LESSON;
  return useMutation(
    (data: any) =>
      axios({
        method: isUpdate ? "put" : "post",
        url,
        params: {
          company_id: companyId,
        },
        data: data,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.E_LEARNING_COURSE_LIST]);
        queryClient.invalidateQueries([QUERY_NAME.LESSON_LIST]);
        queryClient.invalidateQueries([QUERY_NAME.LESSON_DETAIL]);

        notification["success"]({
          message: "保存しました",
        });
        onSubmitSuccess && onSubmitSuccess();
      },
      onError() {
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};

export const useUpdateLessonHistory = (lessonId: string) => {
  const companyId = getCompanyId();
  const queryClient = useQueryClient();

  return useMutation(
    (data: any) =>
      axios({
        method: "post",
        url: `${SERVICES.E_LEARNING_LESSON}/${lessonId}${SERVICES.HISTORIES}`,
        params: {
          company_id: companyId,
        },
        data,
      }),
    {
      onSuccess(_, variables) {
        queryClient.setQueryData(
          [QUERY_NAME.LESSON_DETAIL, companyId, lessonId],
          (oldData: any) => {
            if (!oldData) return oldData;

            return {
              ...oldData,
              data: {
                ...oldData.data,
                history: {
                  ...oldData.data.history,
                  ...variables,
                },
              },
            };
          },
        );

        queryClient.invalidateQueries([QUERY_NAME.COURSE_HISTORIES]);
      },
      onError() {
        notification.error({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};
